import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BellRing,
  CalendarDays,
  Check,
  Clock,
  Heart,
  ListChecks,
  MessageCircle,
} from "lucide-react";
import earlyMailScreen from "@/assets/features/early-mail.webp";
import lateMailScreen from "@/assets/features/late-mail.webp";
import lexiChatScreen from "@/assets/features/lexi-chat.webp";
import myTipsScreen from "@/assets/features/my-tips.webp";
import raceDayScreen from "@/assets/features/race-day-tips.webp";
import raceResultsScreen from "@/assets/features/race-results.webp";
import { IconBadge } from "@/components/ui/icon-badge";
import { raceDayTipsTime } from "@/utils/time-format";

// Sydney time with its zone, like /lexi-pro: "11:00 am AEDT" in daylight saving, "10:00 am AEST" in winter
const RACE_DAY_TIME = raceDayTipsTime();

/*
 * Lexi's Features (home page, rebuilt 2 Nov 2026).
 *
 * Owner: "the images are old and the features we are showing off are not
 * properly presented". What Lexi offers today, each with a real lexi.tips screen
 * (captured on a phone-size browser from races on 1-2 Nov 2026; Sydney times).
 *
 * Copy rules: customer tip names only (Early Mail / Race Day / Late Mail Top Tip,
 * tip 2-4), no profit / guarantee / "value" / advice wording, only the Lexi
 * Social and Lexi Pro plans. Extra Details (experimental) and /analysis are
 * deliberately not featured.
 *
 * `focus` = the part of the screen the feature is about, as % of the screen
 * height; a pink outline is drawn there.
 */
export const FEATURES = [
  {
    id: "early-mail",
    icon: CalendarDays,
    title: "Early Mail",
    short: "Up to 3 days out",
    when: "Up to 3 days before race day",
    plans: [{ label: "Lexi Pro", pro: true }],
    body: "Lexi's first look at the race, up to 3 days out: her Early Mail Top Tip plus tips 2, 3 and 4, with Lexi's own odds for each runner.",
    points: [
      "Early Mail Top Tip and tips 2 to 4 for every race",
      "Arrives up to 3 days before race day",
      "Stays on the race page, so you can see how Lexi's view changes",
    ],
    screen: earlyMailScreen,
    alt: "Lexi screen for Ballarat Race 7 showing the Early Mail, which arrived 2 days before this race, below the Race Day tips",
    caption: "Ballarat R7 · Mon 2 Nov 2026",
    focus: { top: 43.8, height: 27.3 },
  },
  {
    id: "race-day",
    icon: Clock,
    title: "Race Day tips",
    short: `By ${RACE_DAY_TIME}`,
    when: `By ${RACE_DAY_TIME}`,
    plans: [
      { label: "Free: Top Tip + tip 2" },
      { label: "Lexi Pro: all 4", pro: true },
    ],
    body: `Every Australian race, every race day. Lexi's Race Day tips are ready by ${RACE_DAY_TIME}, with the latest price next to each runner.`,
    points: [
      "Race Day Top Tip and tip 2 free with Lexi Social",
      "Tips 3 and 4 with Lexi Pro",
      "Scratched runners are clearly marked",
    ],
    screen: raceDayScreen,
    alt: "Lexi screen for Ballarat Race 7 set to show 2 tips, with the Race Day tips generated at 9:37 am between the Late Mail and the Early Mail",
    caption: "Ballarat R7 · 2-tip view · Mon 2 Nov 2026",
    focus: { top: 27.5, height: 14.5 },
  },
  {
    id: "late-mail",
    icon: BellRing,
    title: "Late Mail",
    short: "Before the jump",
    when: "Minutes before the jump",
    plans: [{ label: "Lexi Pro", pro: true }],
    body: "Lexi's final say. Minutes before the jump she updates her tips for the latest prices and scratchings: a Late Mail Top Tip plus tips 2, 3 and 4.",
    points: [
      "Late Mail Top Tip and tips 2 to 4",
      "Green and red arrows show who Lexi moved up or down since her last tips",
      "Race Day tips and Early Mail stay one tap away",
    ],
    screen: lateMailScreen,
    alt: "Lexi screen for Ballarat Race 7 showing the Late Mail, updated at 4:25 pm, with arrows where Lexi moved runners",
    caption: "Ballarat R7 · Mon 2 Nov 2026",
    focus: { top: 7, height: 25.5 },
  },
  {
    id: "lexi-chat",
    icon: MessageCircle,
    title: "Lexi Chat",
    short: "Ask about any race",
    when: "Under every race",
    plans: [{ label: "Lexi Pro", pro: true }],
    body: "Ask Lexi about the race in front of you: distance, form, jockeys and more. Tap a suggested question or ask your own.",
    points: [
      "Suggested questions to get you started",
      "Type, or tap the mic and talk",
      "Move from race to race with the arrows",
    ],
    screen: lexiChatScreen,
    alt: "Lexi Chat on Ballarat Race 7: asked who has run this distance before, Lexi lists each runner's starts and wins over 1200 metres",
    caption: "Ballarat R7 · Mon 2 Nov 2026 · real answer",
    focus: { top: 45, height: 30.5 },
  },
  {
    id: "results",
    icon: ListChecks,
    title: "Race results",
    short: "Every race, free",
    when: "Every race, open to everyone",
    plans: [{ label: "Free, no sign-in" }],
    body: "One public page with every result. See each race's winner beside Lexi's Early Mail, Race Day and Late Mail tips.",
    points: [
      "Search a horse or track; filter by date, state, track or result",
      "List, Winners, Meetings and Calendar views",
      "Prices are Betfair SP",
    ],
    link: { to: "/race-results", label: "See race results" },
    screen: raceResultsScreen,
    alt: "Lexi race results page with search, filters and four views, and each race's winner beside Lexi's Early Mail, Race Day and Late Mail tips",
    caption: "lexi.tips/race-results",
    focus: { top: 62, height: 25 },
  },
  {
    id: "my-tips",
    icon: Heart,
    title: "My Tips",
    short: "Saved races",
    when: "Make Lexi yours",
    plans: [{ label: "Free with Lexi Social" }],
    body: "Tap the heart on any race to save it to My Tips, sorted by track or by time. Then set Lexi up the way you like it.",
    points: [
      "My Tips: your saved races, by track or by time",
      "Hot Tip price: a flame marks the Top Tip when its price is above the number you set",
      "Tip display: show 2 tips or all 4 (all 4 with Lexi Pro)",
    ],
    screen: myTipsScreen,
    alt: "Lexi My Tips screen with saved races for Ballarat and Ballina, sorted by track",
    caption: "My Tips · Mon 2 Nov 2026",
    focus: { top: 35.5, height: 29 },
  },
];

function PlanBadge({ label, pro }) {
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-bold ${
        pro ? "bg-brand-pink text-white" : "bg-brand-pink/10 text-[#C8007E]"
      }`}
    >
      {label}
    </span>
  );
}

/** A clean phone frame around the real screens; the active one fades in. */
function PhoneScreens({ activeIndex }) {
  const focus = FEATURES[activeIndex].focus;
  return (
    <div className="w-[264px] sm:w-[280px] lg:w-[300px]">
      <div className="rounded-[2.6rem] bg-[#0A0A0A] p-[10px] shadow-[0_30px_60px_-24px_rgba(77,53,68,0.55)] ring-1 ring-black/5">
        <div className="overflow-hidden rounded-[2rem] bg-white">
          <div className="relative h-7 bg-white" aria-hidden="true">
            <span className="absolute left-1/2 top-1.5 h-[18px] w-[76px] -translate-x-1/2 rounded-full bg-[#0A0A0A]" />
          </div>
          <div className="relative aspect-[600/1298]">
            {FEATURES.map((feature, index) => {
              const isActive = index === activeIndex;
              return (
                <img
                  key={feature.id}
                  src={feature.screen}
                  alt={isActive ? feature.alt : ""}
                  aria-hidden={isActive ? undefined : "true"}
                  data-active={isActive ? "true" : "false"}
                  width={600}
                  height={1298}
                  loading="lazy"
                  decoding="async"
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 motion-reduce:transition-none ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                />
              );
            })}
            <span
              aria-hidden="true"
              data-testid="feature-focus"
              className="pointer-events-none absolute left-[2%] right-[2%] rounded-xl border-2 border-brand-pink shadow-[0_0_0_4px_rgba(243,0,153,0.14)] transition-all duration-500 motion-reduce:transition-none"
              style={{ top: `${focus.top}%`, height: `${focus.height}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturesSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef([]);
  const active = FEATURES[activeIndex];

  const selectTab = (index, focus = false) => {
    const next = (index + FEATURES.length) % FEATURES.length;
    setActiveIndex(next);
    if (focus) tabRefs.current[next]?.focus();
  };

  // WAI-ARIA tabs: arrows move between features, Home/End jump to the ends.
  const handleTabKeyDown = (event, index) => {
    const keys = {
      ArrowRight: index + 1,
      ArrowDown: index + 1,
      ArrowLeft: index - 1,
      ArrowUp: index - 1,
      Home: 0,
      End: FEATURES.length - 1,
    };
    if (!(event.key in keys)) return;
    event.preventDefault();
    selectTab(keys[event.key], true);
  };

  return (
    <section
      id="features"
      className="relative overflow-hidden bg-white py-20 md:py-24"
    >
      {/* Background: the site's dot grid and pink glow */}
      <div className="absolute inset-0" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #e5e5e5 1px, transparent 0)`,
            backgroundSize: "24px 24px",
          }}
        />
        <div
          className="absolute left-1/2 top-1/2 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2"
          style={{
            background:
              "radial-gradient(50% 50% at 50% 50%, rgba(255, 20, 147, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
          }}
        />
      </div>

      <div className="container relative mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-sm font-black uppercase tracking-[0.04em] text-brand-pink md:text-base">
            Lexi&apos;s Features
          </p>
          <h2 className="mt-3 text-3xl font-extrabold leading-[1.2] tracking-[-0.01em] text-[#0A0A0A] md:text-4xl">
            Three looks at every race,{" "}
            <span className="italic">and the tools around them</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-gray-600 md:text-lg">
            Early Mail, Race Day tips and Late Mail for every Australian race,
            plus Lexi Chat, public race results and My Tips. Pick a feature to
            see it on a real screen.
          </p>
        </motion.div>

        <div className="mx-auto mt-10 grid max-w-6xl items-start gap-10 lg:mt-14 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div
              role="tablist"
              aria-label="Lexi's features"
              className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3"
            >
              {FEATURES.map((feature, index) => {
                const isActive = index === activeIndex;
                return (
                  <button
                    key={feature.id}
                    ref={(el) => {
                      tabRefs.current[index] = el;
                    }}
                    type="button"
                    role="tab"
                    id={`feature-tab-${feature.id}`}
                    aria-selected={isActive}
                    aria-controls="feature-panel"
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => selectTab(index)}
                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                    data-gtm-element-id={`home_feature_tab-${feature.id}`}
                    className={`flex min-h-[64px] items-center gap-3 rounded-xl border p-2 text-left transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-2 sm:p-3 ${
                      isActive
                        ? "border-brand-pink bg-[#FDF2F8] shadow-sm"
                        : "border-gray-200 bg-white hover:border-brand-pink/40"
                    }`}
                  >
                    <IconBadge icon={feature.icon} />
                    <span className="min-w-0">
                      <span
                        className={`block text-sm font-bold leading-tight sm:text-base ${
                          isActive ? "text-[#C8007E]" : "text-[#0A0A0A]"
                        }`}
                      >
                        {feature.title}
                      </span>
                      <span className="mt-0.5 hidden truncate text-xs text-gray-500 sm:block">
                        {feature.short}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              role="tabpanel"
              id="feature-panel"
              aria-labelledby={`feature-tab-${active.id}`}
              tabIndex={0}
              className="mt-5 rounded-2xl border border-gray-100 bg-white p-6 shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink md:p-8"
            >
              <div className="flex flex-wrap items-center gap-2">
                {active.plans.map((plan) => (
                  <PlanBadge key={plan.label} {...plan} />
                ))}
              </div>
              <h3 className="mt-3 text-2xl font-extrabold tracking-[-0.01em] text-[#0A0A0A]">
                {active.title}
              </h3>
              <p className="mt-1 text-sm font-bold uppercase tracking-[0.04em] text-[#C8007E]">
                {active.when}
              </p>
              <p className="mt-3 leading-relaxed text-gray-600">
                {active.body}
              </p>
              <ul className="mt-4 space-y-2">
                {active.points.map((point) => (
                  <li key={point} className="flex gap-2 text-gray-700">
                    <Check
                      className="mt-0.5 h-5 w-5 shrink-0 text-brand-pink"
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              {active.link && (
                <Link
                  to={active.link.to}
                  data-gtm-element-id="home_feature_results_link-1"
                  className="mt-5 inline-flex items-center gap-1 font-semibold text-[#C8007E] hover:underline"
                >
                  {active.link.label}
                  <ArrowRight
                    className="h-4 w-4"
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                </Link>
              )}
            </div>
          </div>

          <div className="flex flex-col items-center lg:col-span-5">
            <PhoneScreens activeIndex={activeIndex} />
            <p className="mt-3 text-xs text-gray-500">{active.caption}</p>
          </div>
        </div>

        <p className="mx-auto mt-10 max-w-6xl text-center text-xs text-gray-500">
          Real screens from lexi.tips, races on 1 and 2 Nov 2026. Past results
          are no guide to future results.
        </p>
      </div>
    </section>
  );
}

export default FeaturesSection;
